/**
 * E3 · চ্যাট — `/activity/:id/chat`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame E3 (360 × 680).
 *
 * Frame note: "The order is pinned under the header so neither side loses context. Call
 * in the header, because many providers would rather speak than type."
 *
 * WIRED. `GET /v1/activity/:id/messages` supplies the provider's name, the presence line,
 * the context strip, the day divider, the placeholder and every bubble — including each
 * bubble's `১১:৩৮`, which is Bangla numerals and is composed on the server
 * (`apps/api/src/activity/clock.ts`) rather than formatted here.
 *
 * `POST` to the same path sends the customer's own message and answers with the WHOLE
 * thread, which is what is rendered: appending locally is how a client's list and the
 * server's come to disagree. The side of the bubble is not a field the client may send —
 * the statement writes `'me'` as a literal and 0021's insert policy checks it — so a
 * customer cannot put words in the provider's bubble.
 *
 * ONE ACCESSIBLE NAME IS STILL MISSING AND IS NOT INVENTED. The header's call control
 * is named by `call_bn` on the chat payload — E2's drawn `কল করুন`, the same action in
 * the same flow — so no string is written here. The composer's ATTACHMENT glyph has no
 * drawn name anywhere and is not among docs/02's nine, so it stays presentational and
 * the gap is recorded in `docs/bangla-needed/account-activity.md`.
 */
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IconButton } from '@oprappo/ui';
import type { ActivityChat } from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../offering/chrome.js';
import { SurfaceStates } from '../../states/boundary.js';
import { useResource } from '../../data/resource.js';
import { fetchChat, sendChatMessage } from './api.js';

/**
 * THE SCREEN IS A PURE FUNCTION OF THE PAYLOAD.
 *
 * The view takes what the route returned and renders it; the container below does the
 * loading. That split is what lets the fidelity tests render this screen from the same
 * `fixtures/api/*.json` file the API's own suite asserts its response against, so the
 * chain the visual gate depends on is checked end to end: drawn frame == fixture == API
 * output == this markup.
 *
 * `null` means "nothing has arrived yet", and the view renders its chrome and no content
 * rather than substituting design data.
 */
export function E3ChatView({
  data,
  activityRef,
}: {
  data: ActivityChat | null;
  activityRef: string;
}): JSX.Element {
  const navigate = useNavigate();
  const ref = activityRef;

  // The composer's own text, and the conversation as the SERVER last described it.
  // `sent` replaces `data` after a POST because the route answers with the whole thread
  // — appending locally is how a client's list and the server's come to disagree.
  const [draft, setDraft] = useState('');
  const [sent, setSent] = useState<ActivityChat | null>(null);
  const chat = sent ?? data;

  // The call control's accessible name — E2's drawn `কল করুন`, sent on THIS payload
  // (`@oprappo/contracts` § ActivityChat.call_bn) rather than imported from E2's module,
  // so it cannot change under this screen. While the read is in flight there is no name
  // yet and the control is not rendered at all: an unnamed icon button is worse than an
  // absent one, and inventing a name is what docs/01 forbids.
  const callLabelBn = chat?.call_bn ?? null;

  return (
    <Frame className={SAND_FRAME}>
      <div className="bg-white border-b border-border px-[16px] py-[13px] flex gap-[12px] items-center">
        <IconButton control="back" size={20} onClick={() => navigate(-1)} />
        <div className="w-[36px] h-[36px] box-content border border-border rounded-full bg-sand-deep flex-none" />
        <div className="flex-1 min-w-0">
          <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
            {chat?.provider_bn ?? ''}
          </div>
          <div className="font-anek font-normal text-[13px] leading-[1.75] text-success mt-[1px]">
            {chat?.presence_bn ?? ''}
          </div>
        </div>
        {callLabelBn === null ? null : (
          <button
            type="button"
            aria-label={callLabelBn}
            className="bg-transparent border-0 p-0 rounded-none flex-none inline-flex"
          >
            <svg width={20} height={20} viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M4 5c0 8 7 15 15 15l2-3.5-4-2-2 2c-2.5-1.5-4-3-5.5-5.5l2-2-2-4z"
                stroke="#0E3A45"
                strokeWidth={2}
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="bg-sand-deep border-b border-border px-[16px] py-[11px] flex justify-between items-center gap-[11px]">
        <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-body">
          {chat?.context_bn ?? ''}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/activity/${ref}`)}
          className="font-anek font-medium text-[13.5px] leading-[1.75] text-link flex-none bg-transparent border-0 p-0 rounded-none"
        >
          {chat?.view_bn ?? ''}
        </button>
      </div>

      <div className="flex-1 p-[16px] flex flex-col gap-[12px] oprappo-scroll">
        <div className="self-center font-anek font-normal text-[13px] leading-[1.75] text-muted">
          {chat?.day_bn ?? ''}
        </div>
        {(chat?.messages ?? []).map((m) =>
          m.from === 'them' ? (
            <div
              key={m.id}
              className="self-start bg-white border border-border px-[13px] py-[11px] max-w-[80%] box-content"
            >
              <div className="font-anek font-normal text-[15px] leading-[1.75] text-ink2">
                {m.body_bn}
              </div>
              <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[6px]">
                {m.time_bn}
              </div>
            </div>
          ) : (
            <div key={m.id} className="self-end bg-teal px-[13px] py-[11px] max-w-[80%] box-content">
              <div className="font-anek font-normal text-[15px] leading-[1.75] text-white">
                {m.body_bn}
              </div>
              <div className="font-anek font-normal text-[13px] leading-[1.75] text-on-teal-muted mt-[6px]">
                {m.time_bn}
              </div>
            </div>
          ),
        )}
      </div>

      <div className="bg-white border-t border-border px-[16px] py-[11px] flex gap-[11px] items-center">
        {/* No accessible name exists for this control; none is invented. Reported. */}
        <svg width={21} height={21} viewBox="0 0 24 24" fill="none" aria-hidden className="flex-none">
          <path d="M14 4l6 6-9 9H5v-6z" stroke="#0E3A45" strokeWidth={2} strokeLinejoin="round" />
        </svg>
        {/*
          * THE INPUT IS INSIDE THE DRAWN BOX, NOT INSTEAD OF IT.
          *
          * The frame draws the placeholder because it draws an EMPTY composer, and a
          * screen that cannot be typed into cannot send the message the route accepts —
          * so the field has to become an `<input>`. But an input carries the UA's own
          * box, and substituting it for the div moved 2,063 pixels on this frame.
          *
          * So the div keeps the geometry — 44px content-box, a 1px rule, 13px padding,
          * `items-center` — AND keeps the drawn placeholder colour, and the input sits
          * inside it carrying no box at all. `placeholder:` variants are not in the built
          * stylesheet, so the colour stays on the container where the frame puts it and
          * the input paints its own text in ink once there is a draft.
          */}
        <div className="flex-1 min-w-0 border border-border h-[44px] box-content flex items-center px-[13px] font-anek font-normal text-[15px] leading-[1.75] text-muted">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={chat?.placeholder_bn ?? ''}
            aria-label={chat?.placeholder_bn ?? ''}
            className="w-full min-w-0 bg-transparent border-0 p-0 m-0 font-anek font-normal text-[15px] leading-[1.75] text-ink rounded-none outline-none"
          />
        </div>
        {/*
          * The drawn teal square is the BOX; `IconButton` is the control inside it. It
          * renders its own `<button>` with the specified accessible name (`অনুরোধ পাঠান`,
          * docs/02 § accessible names), so wrapping it in a second button would nest one
          * button in another — invalid, and it moved the glyph.
          */}
        <div className="w-[40px] h-[40px] bg-teal flex items-center justify-center flex-none">
          <IconButton
            control="send"
            size={18}
            stroke="#ffffff"
            onClick={() => {
              const body = draft.trim();
              if (body === '') return;
              setDraft('');
              void sendChatMessage(ref, body).then(setSent);
            }}
          />
        </div>
      </div>
    </Frame>
  );
}

export function E3Chat(): JSX.Element {
  const { id } = useParams();
  const ref = id ?? '';
  const load = useCallback((signal: AbortSignal) => fetchChat(ref, signal), [ref]);
  const { data, error } = useResource(load, ref);

  return (
    <SurfaceStates surface="activity" error={error ?? undefined}>
      <E3ChatView data={data} activityRef={ref} />
    </SurfaceStates>
  );
}
